import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getApi, postApi } from '../../Services/apiService';
import { toast } from 'react-toastify';
import PromoCodeModal from '../../Components/PromoCodeModal/PromoCodeModal';

const AutomatedTenantScreening = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const propertyAddress = location.state?.propertyAddress || location.state?.address || 'this property';
  const propertyCode = location.state?.propertyCode || '';
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCheckingPayment, setIsCheckingPayment] = useState(true);
  const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkPaymentStatus = async () => {
      try {
        const response = await getApi(`/property/${id}`);
        const tenantExtra = response?.data?.tenant_extra;
        if (!cancelled && tenantExtra && String(tenantExtra.is_payment) === '1') {
          navigate(`/tenant/properties/${id}/screening/complete-payment`, {
            replace: true,
            state: {
              paymentAlreadyDone: true,
              propertyAddress,
              propertyCode,
            },
          });
          return;
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      } finally {
        if (!cancelled) setIsCheckingPayment(false);
      }
    };

    checkPaymentStatus();
    return () => { cancelled = true; };
  }, [id, navigate, propertyAddress, propertyCode]);

  const steps = [
    {
      circleSrc: '/assets/images/circle1.svg',
      iconSrc: '/assets/images/CallTenantDetail.svg',
      title: 'Automated Voice Interview',
      description: 'Our Automation system will call you to ask questions about employment, rental history, and income. Takes about 5-10 minutes.',
      time: '5-10min',
    },
    {
      circleSrc: '/assets/images/circle2.svg .svg',
      iconSrc: '/assets/images/UploadTenantDetail.svg',
      title: 'Document Upload',
      description: 'Upload pay stubs, bank statements, and ID for AI-powered verification.',
      time: '2min.',
    },
    {
      circleSrc: '/assets/images/circle3.svg .svg',
      iconSrc: '/assets/images/UsersTenantDetail.svg',
      title: 'Reference Verification',
      description: 'Provide previous landlord contacts. AI will verify via phone or text.',
      time: null,
    },
  ];

  const handleProceedToPayment = () => {
    if (isProcessing) return;
    setIsPromoModalOpen(true);
  };

  const startPayment = async (promoCode = '') => {
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      const payload = { property_id: id };
      if (promoCode) {
        payload.code = promoCode;
      }

      const response = await postApi('/subscriptions/create-intent', payload);

      const data = response?.data || response;
      const clientSecret = data?.client_secret || data?.clientSecret || '';
      const listPrice = Number(
        data?.original_amount ?? data?.originalAmount ?? data?.base_amount ?? 9.99
      );
      const amount = Number(data?.amount ?? listPrice);
      const originalAmount = Number.isFinite(listPrice) ? listPrice : 9.99;
      const chargedAmount = Number.isFinite(amount) ? amount : originalAmount;
      const reportedDiscount = Number(
        data?.discount_amount ?? data?.discountAmount ?? data?.amount_off
      );
      const discountAmount = promoCode
        ? (Number.isFinite(reportedDiscount) && reportedDiscount > 0 && reportedDiscount <= originalAmount
          ? reportedDiscount
          : Math.max(0, Math.round((originalAmount - chargedAmount) * 100) / 100))
        : 0;

      if (!clientSecret) {
        throw new Error(response?.message || 'Missing client_secret from tenant screening create-intent');
      }

      setIsPromoModalOpen(false);
      navigate(`/tenant/properties/${id}/screening/complete-payment`, {
        state: {
          landlordName: location.state?.landlordName || 'this landlord',
          propertyAddress,
          propertyCode,
          clientSecret,
          amount: chargedAmount,
          originalAmount,
          discountAmount,
          promoCode,
        },
      });
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to start payment for screening';
      toast.error(msg);
    } finally {
      setIsProcessing(false);
    }
  };

  if (isCheckingPayment) {
    return (
      <div className="automated-screening-page">
        <div className="automated-screening-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="automated-screening-page">
      <div className="automated-screening-content">
        <header className="automated-screening-header">
          <div className="automated-screening-icon-wrap">
            <img
              src="/assets/images/automateStar.svg"
              alt=""
              className="automated-screening-top-icon"
            />
          </div>
          <h1 className="automated-screening-title">Automated Tenant Screening</h1>
          <p className="automated-screening-subtitle">
            Complete your screening to apply for {propertyAddress}.
          </p>
        </header>

        <section className="automated-screening-section">
          <h2 className="automated-screening-section-title">What to Expect</h2>
          <div className="automated-screening-card">
            {steps.map((step) => (
              <div key={step.title} className="automated-screening-step">
                <img
                  src={step.circleSrc}
                  alt=""
                  className="automated-screening-step-circle"
                />
                {step.iconSrc && (
                  <img
                    src={step.iconSrc}
                    alt=""
                    className="automated-screening-step-icon"
                  />
                )}
                <div className="automated-screening-step-body">
                  <h3 className="automated-screening-step-title">{step.title}</h3>
                  <div className="automated-screening-step-row">
                    <p className="automated-screening-step-desc">{step.description}</p>
                    {step.time && (
                      <span className="automated-screening-step-time">{step.time}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="automated-screening-fee-section">
          <div className="automated-screening-fee-card">
            <div className="automated-screening-fee-header">
              <div>
                <h3 className="automated-screening-fee-title">Screening Fee</h3>
                <p className="automated-screening-fee-subtitle">One-time payment for this landlord</p>
              </div>
              <div className="automated-screening-fee-amount">$9.99</div>
            </div>
            <p className="automated-screening-fee-disclaimer">
              Already screened? If you've completed screening for this landlord, you won't be charged again.
            </p>
          </div>
        </section>

        <div className="automated-screening-actions">
          <button
            type="button"
            className="automated-screening-proceed-btn"
            onClick={handleProceedToPayment}
            disabled={isProcessing}
          >
            {isProcessing ? 'Preparing payment...' : 'Proceed to payment'}
          </button>
        </div>

        <p className="automated-screening-footer">
          By proceeding, you agree to our Terms of Service and consent to Automated screening.
        </p>
      </div>

      <PromoCodeModal
        isOpen={isPromoModalOpen}
        onClose={() => setIsPromoModalOpen(false)}
        onApply={(code) => startPayment(code)}
        onSkip={() => startPayment()}
        isSubmitting={isProcessing}
        description="Enter your code to get a discount on the $9.99 screening fee, or skip to continue at the regular price."
      />
    </div>
  );
};

export default AutomatedTenantScreening;
